import { useEditorReveal } from "./editor/react.tsx";
import { useEditorDocuments } from "./editor/documents-react.tsx";
import { useStationWorkbench } from "./state/station/react.tsx";
import type { StationWorkbenchSnapshot } from "./state/station/workbench-model.ts";
import { inferPrimaryFile, type WorkspaceView } from "./state/station/workspace-view.ts";
import "./styles.css";
import type { ReactNode } from "react";


/**
 * Short labels that read better as letterforms than as pictograms, drawn as
 * SVG so they inherit currentColor and the icon box like every other glyph.
 */
function GlyphLabel({ text }: { text: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
      className="shrink-0"
    >
      <text
        x="8"
        y="8"
        textAnchor="middle"
        dominantBaseline="central"
        fill="currentColor"
        fontSize={text.length > 2 ? 8 : 9.5}
        fontWeight="700"
        fontFamily="var(--font-family-mono)"
        letterSpacing="-0.3"
      >
        {text}
      </text>
    </svg>
  );
}

/** Explorers whose name is short enough to serve as its own icon. */
const EXPLORER_GLYPHS: Record<string, string> = {
  workflows: "WF",
  r2: "R2",
  kv: "KV",
  queues: "Qs"
};

const MENU_ITEM_CLASS =
  "flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs text-kumo-default hover:bg-kumo-fill disabled:opacity-40";
import { Button, Input, KumoPortalProvider } from "@cloudflare/kumo";
import { CodeEditor } from "./editor/code-editor.tsx";
import { DatabaseExplorer } from "./resources/database/explorer.tsx";
import { WorkflowExplorer } from "./resources/workflows/WorkflowExplorer.tsx";
import { ContainerExplorer } from "./resources/containers/ContainerExplorer.tsx";
import { R2Explorer } from "./resources/r2/explorer.tsx";
import { KVExplorer } from "./resources/kv/explorer.tsx";
import { KVBrowseSurface } from "./resources/kv/browse.tsx";
import { WorkflowInstancesSurface } from "./resources/workflows/WorkflowInstancesSurface.tsx";
import { WorkflowInstanceSurface } from "./resources/workflows/WorkflowInstanceSurface.tsx";
import { QueueExplorer } from "./resources/queues/explorer.tsx";
import { WorkerBindingsExplorer } from "./resources/worker-bindings/WorkerBindingsExplorer.tsx";
import { HistoryExplorer } from "./resources/history/HistoryExplorer.tsx";
import { TraceExplorer } from "./observability/TraceExplorer";
import { BrowserObservabilityExplorer } from "./observability/BrowserExplorer";
import { useMenuEscape } from "./keybinds/react.tsx";
import { Modal } from "./keybinds/modal.tsx";
import { useInitialRecordingLink, useRecordingAnnouncements } from "./recording/react.tsx";
import {
  EmbeddedRecordingPlayer,
  FlowRecordingsExplorer,
  RecordingPlayerPane,
  OPEN_FLOW_RECORDING_EVENT
} from "./recording/FlowRecordingsExplorer.tsx";
import { ReviewWorkbench } from "./ReviewWorkbench";
import { useReviewNavigation } from "./review/navigation/react.tsx";
import {
  parseReviewDeepLink,
  type ReviewDeepLinkState
} from "@workerstation/shared";
import {
  createReviewEditorTab,
  editorTabAfterReviewExit,
  isReviewEditorTabId,
  projectReviewTabsAcrossWorkspace,
  type ReviewEditorTab
} from "./review-tab-model";
import {
  RESOURCE_TABS,
  type ResourceExplorerKind
} from "./ResourceExplorerTabs";
import {
  type DatabaseTarget,
  databaseTargetKey
} from "./api/database/model.ts";
import { SplitPane } from "./SplitPane";
import { EditorLayout } from "./EditorLayout";
import {
  createEditorLayout,
  editorGroups,
  collapseEmptyGroup,
  closeTab as closeEditorLayoutTab,
  findEditorGroup,
  findEditorTabGroup,
  findAdjacentGroup,
  focusGroup as focusEditorGroup,
  moveTab as moveEditorLayoutTab,
  openOrFocusTab,
  selectEditorTab,
  reorderTab as reorderEditorLayoutTab,
  selectTab as selectEditorLayoutTab,
  splitGroup as splitEditorLayoutGroup,
  setSplitRatio,
  fileEditorTabId,
  type EditorLayoutState,
  type EditorLayoutNode
} from "./editor-layout-model";
import {
  ConsoleSurface,
  HttpSurface,
  PreviewSurface,
  TestSurface,
  type HeaderEntry,
  type HttpMethod
} from "./resources/execution/surfaces.tsx";
import { ChatSurface } from "./chat/ChatSurface.tsx";
import { StationLauncher } from "./chat/StationLauncher.tsx";
import { useExecution } from "./state/execution/react.tsx";
import type { PreviewInit as RunRequestInit } from "./state/execution/model.ts";
import { WorkspaceChangesView } from "./review/workspace-changes/WorkspaceChanges.tsx";
import { useWorkspaceChangesModel } from "./review/workspace-changes/react.tsx";
import { OpencodeThemeProvider } from "./opencode/index.ts";
import {
  FILES_SCOPE,
  activeScopeOf,
  isTabInScope,
  tabScope
} from "./tabs/scope.ts";
import { GlobalCommandPalette } from "./GlobalCommandPalette.tsx";
import { useSurfaceCommands } from "./commands/react.tsx";
import { FileTypeIcon, FolderTypeIcon } from "./file-icons";
import {
  stationHostname,
  stationIdFromHostname,
  workerPreviewBase,
  withWorkerstationBase,
  workerstationBasePath
} from "@workerstation/shared";
import {
  workerNameFromFiles,
  type PlaygroundFiles
} from "./default-workspaces";
import { HighlightedPreview } from "./editor/highlighted-preview.tsx";
import type { editor as monacoEditor } from "monaco-editor";
import {
  ArrowsClockwiseIcon,
  ArrowsLeftRight,
  BracketsCurlyIcon,
  CaretDownIcon,
  CaretRightIcon,
  CircleNotchIcon,
  WarningCircleIcon,
  DatabaseIcon,
  Eye,
  FileSql,
  FlowArrow,
  Flask,
  FolderIcon,
  Key,
  ListBullets,
  MagnifyingGlassIcon,
  Plus,
  PencilSimple,
  TerminalWindow,
  X,
  MoonIcon
} from "@phosphor-icons/react";
import { createPortal } from "react-dom";
import { Effect, Layer, Option } from "effect";
import { parseFlowRecordingRef } from "./recording/initial-link-model.ts";
import { mountApplication } from "./runtime.tsx";
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";



interface DatabaseEditorTab {
  id: string;
  kind: "query" | "table";
  title: string;
  target: DatabaseTarget;
  schemaName?: string;
  tableName?: string;
}

interface WorkflowEditorTab {
  id: string;
  title: string;
  workspaceId: string;
  definition: import("./api/workflows/model.ts").WorkflowDefinitionRecord;
  instance?: import("./api/workflows/model.ts").WorkflowInstanceRecord;
}

interface WorkflowInstancesTab {
  id: string;
  title: string;
  workspaceId: string;
  definition: import("./api/workflows/model.ts").WorkflowDefinitionRecord;
}

interface WorkflowInstanceDetailTab {
  id: string;
  title: string;
  workspaceId: string;
  definition: import("./api/workflows/model.ts").WorkflowDefinitionRecord;
  instanceId: string;
}

interface KVEditorTab {
  id: string;
  title: string;
  workspaceId: string;
  bindingName: string;
  namespace: string;
}

type RuntimeToolKind = "preview" | "http" | "test" | "console";

type WorkbenchTab =
  | { id: string; kind: "file"; filename: string }
  | { id: string; kind: "database"; tab: DatabaseEditorTab }
  | { id: string; kind: "workflow"; tab: WorkflowEditorTab }
  | { id: string; kind: "workflow-instances"; tab: WorkflowInstancesTab }
  | { id: string; kind: "workflow-instance"; tab: WorkflowInstanceDetailTab }
  | { id: string; kind: "kv"; tab: KVEditorTab }
  | { id: string; kind: "review"; tab: ReviewEditorTab }
  | {
      id: string;
      kind: "flow-recording";
      recordingId: string;
      scope: string;
      title: string;
    }
  | { id: `tool:${RuntimeToolKind}`; kind: "tool"; tool: RuntimeToolKind }
  | {
      id: `explorer:${ResourceExplorerKind}`;
      kind: "explorer";
      explorer: ResourceExplorerKind;
    };

const RUNTIME_TOOL_ICONS: Record<RuntimeToolKind, ReactNode> = {
  preview: <Eye size={14} />,
  http: <ArrowsLeftRight size={14} />,
  test: <Flask size={14} />,
  console: <TerminalWindow size={14} />
};

const RUNTIME_TOOL_TABS = [
  { id: "tool:preview", kind: "tool", tool: "preview" },
  { id: "tool:http", kind: "tool", tool: "http" },
  { id: "tool:test", kind: "tool", tool: "test" },
  { id: "tool:console", kind: "tool", tool: "console" }
] as const satisfies readonly WorkbenchTab[];

// Resource explorers are always-available tab descriptors (like runtime tools):
// their icon buttons live in the editor group chrome and open the explorer as a
// full editor tab rather than a sidebar panel.
const RESOURCE_EXPLORER_TABS: readonly Extract<
  WorkbenchTab,
  { kind: "explorer" }
>[] = RESOURCE_TABS.map((tab) => ({
  id: `explorer:${tab.id}`,
  kind: "explorer",
  explorer: tab.id
}));

function editorLayoutTabIds(node: EditorLayoutNode): string[] {
  return node.kind === "group"
    ? [...node.tabs]
    : [
        ...editorLayoutTabIds(node.children[0]),
        ...editorLayoutTabIds(node.children[1])
      ];
}

function editorLayoutGroupCounts(
  node: EditorLayoutNode
): Array<{ id: string; tabCount: number }> {
  return node.kind === "group"
    ? [{ id: node.id, tabCount: node.tabs.length }]
    : [
        ...editorLayoutGroupCounts(node.children[0]),
        ...editorLayoutGroupCounts(node.children[1])
      ];
}

// Secondary panels carry no chrome, so a group that loses its LAST tab is
// auto-collapsed instead of lingering empty. Only groups that transitioned from
// >0 tabs to 0 are removed — a freshly split (born-empty) panel keeps serving as
// a drop target.
function collapseVacatedGroups(
  before: EditorLayoutState,
  after: EditorLayoutState
): EditorLayoutState {
  const priorCounts = new Map(
    editorLayoutGroupCounts(before.root).map((group) => [group.id, group.tabCount])
  );
  const vacated = editorLayoutGroupCounts(after.root).filter(
    (group) => group.tabCount === 0 && (priorCounts.get(group.id) ?? 0) > 0
  );
  let result = after;
  for (const group of vacated) {
    if (editorLayoutGroupCounts(result.root).length <= 1) break;
    if (!findEditorGroup(result, group.id)) continue;
    result = collapseEmptyGroup(result, group.id);
  }
  return result;
}

function initialReviewEditorTab(): ReviewEditorTab | undefined {
  if (typeof window === "undefined") return undefined;
  const parsed = parseReviewDeepLink(window.location.search);
  return parsed.status === "valid"
    ? createReviewEditorTab(parsed.state)
    : undefined;
}




function workerEntryFromFiles(files: PlaygroundFiles): string {
  try {
    const pkg = JSON.parse(files["package.json"] ?? "{}") as {
      main?: unknown;
    };
    if (typeof pkg.main === "string" && files[pkg.main] !== undefined) {
      return pkg.main;
    }
  } catch {
    // Fall through to conventional entrypoints.
  }
  return (
    [
      "src/index.ts",
      "src/index.tsx",
      "src/index.js",
      "src/index.jsx",
      "index.ts",
      "index.js"
    ].find((file) => files[file] !== undefined) ?? "src/index.ts"
  );
}

function getContentType(headers: Record<string, string>) {
  const match = Object.entries(headers).find(
    ([key]) => key.toLowerCase() === "content-type"
  );
  return match?.[1] ?? "text/plain";
}

interface TreeFolderNode {
  type: "folder";
  name: string;
  path: string;
  children: TreeNode[];
}

interface TreeFileNode {
  type: "file";
  name: string;
  path: string;
}

type TreeNode = TreeFolderNode | TreeFileNode;

interface SearchMatch {
  file: string;
  line: number;
  text: string;
  start: number;
  end: number;
}

interface SearchGroup {
  file: string;
  matches: SearchMatch[];
}

const MAX_MATCHES_PER_FILE = 50;

function searchFileContents(
  files: PlaygroundFiles,
  rawQuery: string
): { groups: SearchGroup[]; total: number } {
  const q = rawQuery.toLowerCase();
  const groups: SearchGroup[] = [];
  let total = 0;

  for (const [filePath, contents] of Object.entries(files)) {
    const lines = contents.split("\n");
    const matches: SearchMatch[] = [];

    for (
      let i = 0;
      i < lines.length && matches.length < MAX_MATCHES_PER_FILE;
      i++
    ) {
      const lower = lines[i].toLowerCase();
      let from = 0;
      let idx = lower.indexOf(q, from);
      while (idx !== -1 && matches.length < MAX_MATCHES_PER_FILE) {
        matches.push({
          file: filePath,
          line: i + 1,
          text: lines[i],
          start: idx,
          end: idx + q.length
        });
        from = idx + Math.max(q.length, 1);
        idx = lower.indexOf(q, from);
      }
    }

    if (matches.length) {
      groups.push({ file: filePath, matches });
      total += matches.length;
    }
  }

  return { groups, total };
}

// Trim leading whitespace and clip long prefixes so the highlighted match
// stays visible inside the narrow sidebar row, like VS Code's search view.
function matchPreview(match: SearchMatch) {
  const trimmed = match.text.replace(/^\s+/, "");
  const offset = match.text.length - trimmed.length;
  let text = trimmed;
  let start = Math.max(0, match.start - offset);
  let end = Math.max(start, match.end - offset);

  const MAX_BEFORE = 20;
  if (start > MAX_BEFORE) {
    const cut = start - MAX_BEFORE;
    text = `…${text.slice(cut)}`;
    start = start - cut + 1;
    end = end - cut + 1;
  }

  end = Math.min(end, text.length);
  return {
    before: text.slice(0, start),
    match: text.slice(start, end),
    after: text.slice(end)
  };
}

function buildFileTree(paths: readonly string[], extraFolders: readonly string[]): TreeNode[] {
  const root: TreeNode[] = [];
  const folders = new Map<string, TreeFolderNode>();

  function ensureFolder(path: string): TreeFolderNode {
    const existing = folders.get(path);
    if (existing) return existing;
    const folder: TreeFolderNode = {
      type: "folder",
      name: path.split("/").pop() ?? path,
      path,
      children: []
    };
    folders.set(path, folder);
    const parentPath = path.includes("/")
      ? path.slice(0, path.lastIndexOf("/"))
      : "";
    if (parentPath) {
      ensureFolder(parentPath).children.push(folder);
    } else {
      root.push(folder);
    }
    return folder;
  }

  // Explicit folders first so empty ones exist even with no files inside.
  for (const folderPath of extraFolders) {
    ensureFolder(folderPath);
  }

  for (const filePath of paths) {
    const file: TreeFileNode = {
      type: "file",
      name: filePath.split("/").pop() ?? filePath,
      path: filePath
    };
    const dir = filePath.includes("/")
      ? filePath.slice(0, filePath.lastIndexOf("/"))
      : "";
    if (dir) {
      ensureFolder(dir).children.push(file);
    } else {
      root.push(file);
    }
  }

  function sortNodes(nodes: TreeNode[]) {
    nodes.sort((a, b) =>
      a.type === b.type
        ? a.name.localeCompare(b.name)
        : a.type === "folder"
          ? -1
          : 1
    );
    for (const node of nodes) {
      if (node.type === "folder") sortNodes(node.children);
    }
  }
  sortNodes(root);

  return root;
}

function FileTreeRows({
  nodes,
  depth,
  collapsed,
  onToggle,
  currentFile,
  onOpen,
  onDelete,
  onRename,
  onFileContextMenu,
  onFolderContextMenu
}: {
  nodes: TreeNode[];
  depth: number;
  collapsed: Set<string>;
  onToggle: (path: string) => void;
  currentFile: string | undefined;
  onOpen: (path: string) => void;
  onDelete: (path: string) => void;
  onRename: (path: string) => void;
  onFileContextMenu: (path: string, x: number, y: number) => void;
  onFolderContextMenu: (path: string, x: number, y: number) => void;
}) {
  return (
    <>
      {nodes.map((node) =>
        node.type === "folder" ? (
          <div key={node.path}>
            <button
              type="button"
              onClick={() => onToggle(node.path)}
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onFolderContextMenu(node.path, e.clientX, e.clientY);
              }}
              className="flex w-full cursor-pointer items-center gap-1 bg-transparent py-[3px] pr-2 text-xs text-kumo-default"
              style={{ paddingLeft: 8 + depth * 12 }}
            >
              {collapsed.has(node.path) ? (
                <CaretRightIcon size={11} className="shrink-0" />
              ) : (
                <CaretDownIcon size={11} className="shrink-0" />
              )}
              <FolderTypeIcon
                open={!collapsed.has(node.path)}
                className="h-[15px] w-[15px]"
              />
              <span className="truncate">{node.name}</span>
            </button>
            {!collapsed.has(node.path) ? (
              <FileTreeRows
                nodes={node.children}
                depth={depth + 1}
                collapsed={collapsed}
                onToggle={onToggle}
                currentFile={currentFile}
                onOpen={onOpen}
                onDelete={onDelete}
                onRename={onRename}
                onFileContextMenu={onFileContextMenu}
                onFolderContextMenu={onFolderContextMenu}
              />
            ) : null}
          </div>
        ) : (
          <div
            key={node.path}
            role="button"
            tabIndex={0}
            data-file-path={node.path}
            aria-current={node.path === currentFile ? "page" : undefined}
            onClick={() => onOpen(node.path)}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onFileContextMenu(node.path, e.clientX, e.clientY);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") onOpen(node.path);
              if (e.key === "F2") {
                e.preventDefault();
                onRename(node.path);
              }
            }}
            className={`group flex w-full cursor-pointer items-center gap-1.5 py-[3px] pr-2 font-mono text-xs ${
              node.path === currentFile
                ? "bg-kumo-elevated text-kumo-default"
                : "text-kumo-subtle hover:text-kumo-default"
            }`}
            style={{ paddingLeft: 8 + depth * 12 + 15 }}
          >
            <FileTypeIcon path={node.path} className="h-[14px] w-[14px]" />
            <span className="truncate">{node.name}</span>
            {node.path !== "package.json" ? (
              <button
                type="button"
                aria-label={`Remove ${node.path}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(node.path);
                }}
                className="ml-auto hidden shrink-0 cursor-pointer bg-transparent p-0.5 text-kumo-subtle group-hover:block"
              >
                <X size={11} />
              </button>
            ) : null}
          </div>
        )
      )}
    </>
  );
}

// SQL table/query surfaces are loaded only when a database editor tab opens.
const StudioEditorSurface = lazy(() => import("studio-native"));
const WorkflowVisualization = lazy(() =>
  import("./resources/workflows/WorkflowVisualization.tsx").then((module) => ({
    default: module.WorkflowVisualization
  }))
);




export function App() {
  const editorDocuments = useEditorDocuments();
  const { files, folders, setFiles, setFolders } = editorDocuments;
  const [currentFile, setCurrentFile] = useState<string | undefined>(
    inferPrimaryFile(files)
  );
  const [openTabs, setOpenTabs] = useState<string[]>(() => {
    const primary = inferPrimaryFile(files);
    return primary ? [primary] : [];
  });
  const [editorLayout, setEditorLayout] = useState<EditorLayoutState>(() => {
    const primary = inferPrimaryFile(files);
    const review = initialReviewEditorTab();
    const tabs = [
      ...(primary ? [fileEditorTabId(primary)] : []),
      ...(review ? [review.id] : [])
    ];
    return createEditorLayout(tabs, review?.id ?? tabs[0] ?? null);
  });
  const editorLayoutRef = useRef(editorLayout);
  editorLayoutRef.current = editorLayout;
  const editorTabOrder = useMemo(
    () => editorLayoutTabIds(editorLayout.root),
    [editorLayout]
  );
  const focusedEditorGroup = findEditorGroup(
    editorLayout,
    editorLayout.focusedGroupId
  );
  const activeEditorTab = focusedEditorGroup?.activeTabId ?? null;
  const editorTabOrderRef = useRef(editorTabOrder);
  editorTabOrderRef.current = editorTabOrder;
  const [databaseTabs, setDatabaseTabs] = useState<DatabaseEditorTab[]>([]);
  const [recordingTabs, setRecordingTabs] = useState<
    { id: string; recordingId: string; scope: string; title: string }[]
  >([]);
  const [workflowTabs, setWorkflowTabs] = useState<WorkflowEditorTab[]>([]);
  const [workflowInstancesTabs, setWorkflowInstancesTabs] = useState<
    WorkflowInstancesTab[]
  >([]);
  const [workflowInstanceDetailTabs, setWorkflowInstanceDetailTabs] = useState<
    WorkflowInstanceDetailTab[]
  >([]);
  const [kvTabs, setKVTabs] = useState<KVEditorTab[]>([]);
  const [reviewTabs, setReviewTabs] = useState<ReviewEditorTab[]>(() => {
    const review = initialReviewEditorTab();
    return review ? [review] : [];
  });
  const reviewTabsRef = useRef(reviewTabs);
  reviewTabsRef.current = reviewTabs;
  const activeEditorTabRef = useRef(activeEditorTab);
  activeEditorTabRef.current = activeEditorTab;
  const lastNonReviewEditorTabRef = useRef<string | null>(null);
  if (activeEditorTab !== null && !isReviewEditorTabId(activeEditorTab)) {
    lastNonReviewEditorTabRef.current = activeEditorTab;
  }
  const databaseQueryCounterRef = useRef(1);
  const freshPreviewHydrationRef = useRef(0);
  const [addFileOpen, setAddFileOpen] = useState(false);
  const [addFileName, setAddFileName] = useState("");
  const [renameFileOpen, setRenameFileOpen] = useState(false);
  const [renameFileSource, setRenameFileSource] = useState<string | null>(null);
  const [renameFileName, setRenameFileName] = useState("");

  // Workspace persistence
  const workbench = useStationWorkbench(hydrateWorkbench);
  const { booted, bootstrapError, showLauncher, workspaces: workspaceList, activeWorkspace, phase: saveState } = workbench.snapshot;
  const [hydratedSequence, setHydratedSequence] = useState(0);
  const hydrationSequence = workbench.snapshot.hydration?.sequence ?? 0;
  // Chat has no tab to open, so "Focus Chat" moves focus into its composer.
  const [rightPaneTab, setRightPaneTab] = useState<"changes" | "files">("files");
  // Bumped whenever a turn may have touched workspace files, so the changes
  // tab refetches its baseline without the user reloading.
  const [changesToken, setChangesToken] = useState(0);
  const workspaceChanges = useWorkspaceChangesModel({
    workspaceId: booted && !bootstrapError && !showLauncher ? activeWorkspace : null,
    refreshToken: changesToken
  }, files);
  const changedCount = workspaceChanges.summary?.changes.length ?? null;
  const activeToolTab = editorGroups(editorLayout)
    .find((group) => group.id === editorLayout.focusedGroupId)
    ?.activeTabId;
  const activeScope = activeScopeOf(activeToolTab);
  const scopeExplorer = activeScope.startsWith("explorer:")
    ? (activeScope.slice("explorer:".length) as ResourceExplorerKind)
    : null;
  const toolSurfaceActive = activeScope !== FILES_SCOPE;
  const chatPaneRef = useRef<HTMLDivElement | null>(null);
  const focusChatPaneRef = useRef(() => {
    chatPaneRef.current?.querySelector("textarea")?.focus();
  });
  // Kumo portals (dialogs, dropdowns, tooltips) must render above the
  // EditorLayout stable tab panels, which are fixed elements with z-index 1.
  const [kumoPortalContainer, setKumoPortalContainer] =
    useState<HTMLDivElement | null>(null);
  const workspaceIdRef = useRef<string | null>(null);
  const skipNextSaveRef = useRef(true);

  // Multi-workspace session (Slack-style): the URL identifies the session;
  // its workspace list + active workspace live in the Session DO, so the
  // same URL shows the same rail everywhere.
  const sessionIdRef = useRef<string | null>(null);
  sessionIdRef.current = workbench.snapshot.stationId;
  const layoutInteractionSequenceRef = useRef(0);
  const layoutInteractionAtNavigationStartRef = useRef(Infinity);
  const dragIndexRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [railMenu, setRailMenu] = useState<{
    x: number;
    y: number;
    id: string;
  } | null>(null);

  useMenuEscape(railMenu, () => setRailMenu(null));
  const reviewNavigation = useReviewNavigation(activeEditorTab, (parsed) => {
    if (parsed.status === "valid") {
      openReviewDeepLink(parsed.state);
      return;
    }
    const current = editorLayoutRef.current;
    const active = findEditorGroup(
      current,
      current.focusedGroupId
    )?.activeTabId ?? null;
    const nextId = editorTabAfterReviewExit(
      editorLayoutTabIds(current.root),
      lastNonReviewEditorTabRef.current,
      active
    );
    if (nextId) commitEditorLayout(selectEditorLayoutTab(current, nextId));
  });

  // Runtime state is application-scoped so closing or moving a tool tab keeps
  // in-flight work and displayed results alive.
  const [path, setPath] = useState("/");
  const execution = useExecution({
    target: hydratedSequence === hydrationSequence && workbench.snapshot.workspaceId && workbench.snapshot.stationId
      ? { workspaceId: workbench.snapshot.workspaceId, stationId: workbench.snapshot.stationId } : null,
    files, path, booted, hydrationSequence, freshPreviewSequence: workbench.snapshot.freshPreviewSequence
  });
  const { watchMode, testWatch, inFileConcurrency, testing, testResult, testError,
    result, error, running, runId, consoleSource } = execution.snapshot;
  const setConsoleSource = (source: "worker" | "test") => execution.dispatch({ _tag: "Console", source });
  const setInFileConcurrency = (enabled: boolean) => execution.dispatch({ _tag: "Parallel", enabled });
  const [method, setMethod] = useState<HttpMethod>("GET");
  const [reqHeaders, setReqHeaders] = useState<HeaderEntry[]>([]);
  const [reqBody, setReqBody] = useState("");

  // File explorer state
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  // Hamburger "open…" menu: which group to open surfaces into, plus the group's
  // active tab (so Format can be disabled for non-file tabs).
  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(
    new Set()
  );

  // "View recording" cross-links (Browser observability session rows) emit
  // this CustomEvent because sibling explorers cannot reach the tab registry
  // state owned here. FlowRecordingsExplorer listens for the same event to
  // apply the session filter; this listener only opens its explorer tab.
  useRecordingAnnouncements(() => openResourceExplorer("flow-recordings"));

  // Shareable "watch" URL: ?flowRecording=<workspace>:<recordingId> opens the
  // Flow recordings explorer straight into the player for that recording, so a
  // link plays the capture in a session-replay/video player (PostHog-style).
  const flowRecordingDeepLink = useInitialRecordingLink(
    booted && hydrationSequence > 0 && hydratedSequence >= hydrationSequence,
    reference => {
      openResourceExplorer("flow-recordings");
      openFlowRecordingTab(reference.recordingId, reference.workspaceId.toLowerCase(), "Recording");
    }
  );
  const [treeMenu, setTreeMenu] = useState<{
    x: number;
    y: number;
    file?: string;
    folder?: string;
  } | null>(null);

  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");

  useMenuEscape(treeMenu, () => setTreeMenu(null));

  const editorRefs = useRef(
    new Map<string, monacoEditor.IStandaloneCodeEditor>()
  );
  const editorViewStateScope = useRef<object>({});
  const revealEditor = useEditorReveal({
    current: () => ({ workspaceId: workspaceIdRef.current, scope: editorViewStateScope.current }),
    openFile,
    findEditor: id => editorRefs.current.get(id)
  });
  const editorViewStates = useRef(
    new Map<string, monacoEditor.ICodeEditorViewState | null>()
  );
  const workbenchTabs = useMemo<WorkbenchTab[]>(
    () => [
      ...openTabs.map((filename) => ({
        id: fileEditorTabId(filename),
        kind: "file" as const,
        filename
      })),
      ...databaseTabs.map((tab) => ({
        id: tab.id,
        kind: "database" as const,
        tab
      })),
      ...workflowTabs.map((tab) => ({
        id: tab.id,
        kind: "workflow" as const,
        tab
      })),
      ...workflowInstancesTabs.map((tab) => ({
        id: tab.id,
        kind: "workflow-instances" as const,
        tab
      })),
      ...workflowInstanceDetailTabs.map((tab) => ({
        id: tab.id,
        kind: "workflow-instance" as const,
        tab
      })),
      ...kvTabs.map((tab) => ({
        id: tab.id,
        kind: "kv" as const,
        tab
      })),
      ...reviewTabs.map((tab) => ({
        id: tab.id,
        kind: "review" as const,
        tab
      })),
      ...recordingTabs.map((tab) => ({
        id: tab.id,
        kind: "flow-recording" as const,
        recordingId: tab.recordingId,
        scope: tab.scope,
        title: tab.title
      })),
      ...RUNTIME_TOOL_TABS,
      ...RESOURCE_EXPLORER_TABS
    ],
    [
      databaseTabs,
      kvTabs,
      openTabs,
      recordingTabs,
      reviewTabs,
      workflowInstanceDetailTabs,
      workflowInstancesTabs,
      workflowTabs
    ]
  );
  const orderedFiles = useMemo(() => Object.keys(files), [files]);
  const fileTree = useMemo(
    () => buildFileTree(orderedFiles, folders),
    [orderedFiles, folders]
  );
  const searchResults = useMemo(() => {
    const q = query.trim();
    if (!q) return null;
    return searchFileContents(files, q);
  }, [query, files]);

  function toggleFolder(path: string) {
    setCollapsedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }

  function hydrateWorkbench(snapshot: StationWorkbenchSnapshot) {
    const event = snapshot.hydration;
    if (!event) return;
    editorDocuments.hydrate({ sequence: event.sequence, workspaceId: event.workspaceId, files: event.view.files, folders: event.view.folders }, () => {
    setHydratedSequence(event.sequence);
    const fresh = snapshot.freshPreviewSequence > freshPreviewHydrationRef.current;
    if (fresh) {
      freshPreviewHydrationRef.current = snapshot.freshPreviewSequence;
    }
    hydrateWorkspace(event.workspaceId, event.view, event.previousWorkspaceId);
    if (fresh) commitEditorLayout(current => openOrFocusTab(current, "tool:preview"));
    });
  }

  function hydrateWorkspace(
    id: string,
    state: WorkspaceView,
    previousWorkspaceId: string | null
  ) {
    const nextOpenTabs = [...state.openTabs], nextCurrent = state.currentFile;
    workspaceIdRef.current = id;
    editorViewStateScope.current = {};
    editorViewStates.current = new Map();
    skipNextSaveRef.current = true;

    setOpenTabs(nextOpenTabs);
    const nextFileTabIds = nextOpenTabs.map(fileEditorTabId);
    const fallbackFileTab = nextCurrent
      ? fileEditorTabId(nextCurrent)
      : null;
    const projection = projectReviewTabsAcrossWorkspace(
      nextFileTabIds,
      reviewTabsRef.current,
      activeEditorTabRef.current,
      fallbackFileTab
    );
    lastNonReviewEditorTabRef.current = fallbackFileTab;
    // Reloading the same workspace (e.g. after the chat agent patched it)
    // must not close the singleton tool and explorer tabs the user opened —
    // they are workbench surfaces, not files.
    const retainedSurfaces =
      id === previousWorkspaceId
        ? editorGroups(editorLayoutRef.current)
            .flatMap((group) => group.tabs)
            .filter(
              (tabId) =>
                tabId.startsWith("tool:") || tabId.startsWith("explorer:")
            )
        : [];
    // Workflow tabs are pinned to the workspace they were opened from, so a
    // workspace switch must not close them — their actions keep routing to
    // that workspace regardless of which one is active.
    const retainedWorkflowTabs = editorGroups(editorLayoutRef.current)
      .flatMap((group) => group.tabs)
      .filter(
        (tabId) =>
          tabId.startsWith("workflow:") ||
          tabId.startsWith("workflow-instances:") ||
          tabId.startsWith("workflow-instance:")
      );
    const nextEditorTabOrder = [
      ...projection.editorTabOrder,
      ...[...retainedSurfaces, ...retainedWorkflowTabs].filter(
        (tabId) => !projection.editorTabOrder.includes(tabId)
      )
    ];
    // A hydrate can land after the user has already focused a retained
    // workflow tab (session writes serialize, so the hydrate may be late).
    // Keep that explicit selection instead of stealing focus for the
    // incoming workspace's file tab — but only when the user actually
    // touched the layout after the navigation started; a plain switch
    // should land on the incoming workspace's file.
    const userAdjustedLayoutDuringNavigation =
      layoutInteractionSequenceRef.current >
      layoutInteractionAtNavigationStartRef.current;
    layoutInteractionAtNavigationStartRef.current = Infinity;
    const currentActiveEditorTab = activeEditorTabRef.current;
    const nextActiveEditorTab =
      userAdjustedLayoutDuringNavigation &&
      currentActiveEditorTab &&
      retainedWorkflowTabs.includes(currentActiveEditorTab) &&
      nextEditorTabOrder.includes(currentActiveEditorTab)
        ? currentActiveEditorTab
        : projection.activeEditorTab;
    const nextLayout = createEditorLayout(
      nextEditorTabOrder,
      nextActiveEditorTab
    );
    editorLayoutRef.current = nextLayout;
    setEditorLayout(nextLayout);
    setCurrentFile(nextCurrent);
    setDatabaseTabs([]);
    setKVTabs([]);
    databaseQueryCounterRef.current = 1;
    setPath("/");
  }

  function openWorkspace(id: string) {
    if (id !== workspaceIdRef.current) layoutInteractionAtNavigationStartRef.current = layoutInteractionSequenceRef.current;
    workbench.dispatch({ _tag: "Open", workspaceId: id });
  }
  function addWorkspace() { workbench.dispatch({ _tag: "Add" }); }
  function deleteWorkspace(id: string) { workbench.dispatch({ _tag: "Delete", workspaceId: id }); }
  function reloadActiveWorkspace() { workbench.dispatch({ _tag: "Reload" }); }

  // The domain captures the workspace identity and owns the 800ms debounce.
  useEffect(() => {
    if (!booted || !workspaceIdRef.current || hydratedSequence !== hydrationSequence) return;
    if (skipNextSaveRef.current) { skipNextSaveRef.current = false; return; }
    workbench.dispatch({ _tag: "SaveEdit", workspaceId: workspaceIdRef.current,
      draft: { files, openTabs, ...(currentFile === undefined ? {} : { currentFile }), folders } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booted, hydratedSequence, hydrationSequence, files, openTabs, currentFile, folders]);

  function synchronizeFocusedTab(next: EditorLayoutState): void {
    const activeTabId = findEditorGroup(
      next,
      next.focusedGroupId
    )?.activeTabId ?? null;
    activeEditorTabRef.current = activeTabId;
    if (activeTabId !== null && !isReviewEditorTabId(activeTabId)) {
      lastNonReviewEditorTabRef.current = activeTabId;
    }
  }

  function commitEditorLayout(
    update: EditorLayoutState | ((current: EditorLayoutState) => EditorLayoutState)
  ): EditorLayoutState {
    layoutInteractionSequenceRef.current += 1;
    const current = editorLayoutRef.current;
    const updated = typeof update === "function" ? update(current) : update;
    const next = collapseVacatedGroups(current, updated);
    editorLayoutRef.current = next;
    synchronizeFocusedTab(next);
    setEditorLayout(next);
    return next;
  }

  function navigateFocusedReview(
    next: EditorLayoutState,
    mode: "push" | "replace" = "replace"
  ): void {
    const activeTabId = findEditorGroup(
      next,
      next.focusedGroupId
    )?.activeTabId ?? null;
    const review = reviewTabsRef.current.find((tab) => tab.id === activeTabId);
    const controller = reviewNavigation;
    if (review && controller) controller.navigate(review.state, mode);
  }

  function moveWorkbenchTab(
    tabId: string,
    targetGroupId: string,
    targetIndex?: number
  ): void {
    const next = commitEditorLayout((current) =>
      moveEditorLayoutTab(current, tabId, targetGroupId, targetIndex)
    );
    navigateFocusedReview(next);
  }

  function focusWorkbenchGroup(groupId: string) {
    const current = editorLayoutRef.current;
    const group = findEditorGroup(current, groupId);
    if (!group) return;
    const review = reviewTabsRef.current.find(
      (tab) => tab.id === group.activeTabId
    );
    if (review) {
      const controller = reviewNavigation;
      if (controller) controller.navigate(review.state, "replace");
      else commitEditorLayout(focusEditorGroup(current, groupId));
      return;
    }
    commitEditorLayout(focusEditorGroup(current, groupId));
  }

  // Files, resources, reviews, and runtime tools share one grouped layout.
  function openReviewDeepLink(state: ReviewDeepLinkState) {
    const tab = createReviewEditorTab(state);
    const upsertReview = (current: ReviewEditorTab[]) =>
      current.some((entry) => entry.id === tab.id)
        ? current.map((entry) => (entry.id === tab.id ? tab : entry))
        : [...current, tab];
    reviewTabsRef.current = upsertReview(reviewTabsRef.current);
    setReviewTabs((current) => {
      const next = upsertReview(current);
      reviewTabsRef.current = next;
      return next;
    });
    commitEditorLayout((current) => openOrFocusTab(current, tab.id));
  }

  function navigateReview(state: ReviewDeepLinkState) {
    const controller = reviewNavigation;
    if (controller) controller.navigate(state);
    else openReviewDeepLink(state);
  }

  function activateReviewTab(tab: ReviewEditorTab, groupId?: string) {
    if (groupId) {
      commitEditorLayout((current) =>
        selectEditorLayoutTab(current, groupId, tab.id)
      );
    }
    const controller = reviewNavigation;
    if (controller) controller.navigate(tab.state);
    else commitEditorLayout((current) => selectEditorLayoutTab(current, tab.id));
  }

  function openFile(filename: string) {
    const tabId = fileEditorTabId(filename);
    setOpenTabs((prev) =>
      prev.includes(filename) ? prev : [...prev, filename]
    );
    commitEditorLayout((current) => openOrFocusTab(current, tabId));
    setCurrentFile(filename);
  }

  function openDatabaseQuery(target: DatabaseTarget) {
    const number = databaseQueryCounterRef.current++;
    const tab: DatabaseEditorTab = {
      id: `database-query:${number}:${databaseTargetKey(target)}`,
      kind: "query",
      title: `Query ${number}`,
      target
    };
    setDatabaseTabs((current) => [...current, tab]);
    commitEditorLayout((current) => openOrFocusTab(current, tab.id));
  }

  function openDatabaseTable(
    target: DatabaseTarget,
    schemaName: string,
    tableName: string
  ) {
    const id = `database-table:${databaseTargetKey(target)}:${JSON.stringify([
      schemaName,
      tableName
    ])}`;
    setDatabaseTabs((current) =>
      current.some((tab) => tab.id === id)
        ? current
        : [
            ...current,
            {
              id,
              kind: "table",
              title: tableName,
              target,
              schemaName,
              tableName
            }
          ]
    );
    commitEditorLayout((current) => openOrFocusTab(current, id));
  }

  function openFlowRecordingTab(
    recordingId: string,
    scope: string,
    title: string
  ) {
    const id = `flow-recording:${scope}:${recordingId}`;
    setRecordingTabs((current) =>
      current.some((tab) => tab.id === id)
        ? current
        : [...current, { id, recordingId, scope, title }]
    );
    commitEditorLayout((current) => openOrFocusTab(current, id));
  }

  function openWorkflow(
    definition: import("./api/workflows/model.ts").WorkflowDefinitionRecord,
    instance?: import("./api/workflows/model.ts").WorkflowInstanceRecord,
    sourceWorkspaceId?: string
  ) {
    const workspaceId =
      sourceWorkspaceId || definition.routing.workspaceId || activeWorkspace;
    if (!workspaceId) return;
    const id = instance
      ? `workflow:${workspaceId}:${definition.className}:${instance.id}`
      : `workflow:${workspaceId}:${definition.className}:definition`;
    const tab: WorkflowEditorTab = {
      id,
      title: instance
        ? `${definition.className} · ${instance.id.slice(0, 8)}`
        : definition.className,
      workspaceId,
      definition,
      instance
    };
    setWorkflowTabs((current) => {
      const exists = current.some((entry) => entry.id === id);
      return exists
        ? current.map((entry) => (entry.id === id ? tab : entry))
        : [...current, tab];
    });
    commitEditorLayout((current) => openOrFocusTab(current, id));
  }

  function closeWorkflowTab(id: string) {
    setWorkflowTabs((current) => current.filter((tab) => tab.id !== id));
    commitEditorLayout((current) => closeEditorLayoutTab(current, id));
  }

  function openWorkflowInstances(
    definition: import("./api/workflows/model.ts").WorkflowDefinitionRecord,
    sourceWorkspaceId?: string
  ) {
    const workspaceId =
      sourceWorkspaceId || definition.routing.workspaceId || activeWorkspace;
    if (!workspaceId) return;
    const id = `workflow-instances:${workspaceId}:${definition.className}`;
    const tab: WorkflowInstancesTab = {
      id,
      title: `${definition.className} · Instances`,
      workspaceId,
      definition
    };
    setWorkflowInstancesTabs((current) =>
      current.some((entry) => entry.id === id)
        ? current.map((entry) => (entry.id === id ? tab : entry))
        : [...current, tab]
    );
    commitEditorLayout((current) => openOrFocusTab(current, id));
  }

  function closeWorkflowInstancesTab(id: string) {
    setWorkflowInstancesTabs((current) =>
      current.filter((tab) => tab.id !== id)
    );
    commitEditorLayout((current) => closeEditorLayoutTab(current, id));
  }

  function openWorkflowInstanceDetail(
    definition: import("./api/workflows/model.ts").WorkflowDefinitionRecord,
    instanceId: string,
    sourceWorkspaceId?: string
  ) {
    const workspaceId =
      sourceWorkspaceId || definition.routing.workspaceId || activeWorkspace;
    if (!workspaceId) return;
    const id = `workflow-instance:${workspaceId}:${definition.className}:${instanceId}`;
    const tab: WorkflowInstanceDetailTab = {
      id,
      title: `${definition.className} · ${instanceId.slice(0, 8)}`,
      workspaceId,
      definition,
      instanceId
    };
    setWorkflowInstanceDetailTabs((current) =>
      current.some((entry) => entry.id === id)
        ? current.map((entry) => (entry.id === id ? tab : entry))
        : [...current, tab]
    );
    commitEditorLayout((current) => openOrFocusTab(current, id));
  }

  function closeWorkflowInstanceDetailTab(id: string) {
    setWorkflowInstanceDetailTabs((current) =>
      current.filter((tab) => tab.id !== id)
    );
    commitEditorLayout((current) => closeEditorLayoutTab(current, id));
  }

  function openKVBrowse(binding: import("./api/kv/model.ts").KVBindingRecord) {
    const workspaceId = activeWorkspace;
    if (!workspaceId) return;
    const id = `kv:${workspaceId}:${binding.bindingName}`;
    const tab: KVEditorTab = {
      id,
      title: binding.bindingName,
      workspaceId,
      bindingName: binding.bindingName,
      namespace: binding.namespace
    };
    setKVTabs((current) =>
      current.some((entry) => entry.id === id)
        ? current.map((entry) => (entry.id === id ? tab : entry))
        : [...current, tab]
    );
    commitEditorLayout((current) => openOrFocusTab(current, id));
  }

  function closeKVTab(id: string) {
    setKVTabs((current) => current.filter((tab) => tab.id !== id));
    commitEditorLayout((current) => closeEditorLayoutTab(current, id));
  }

  function closeReviewTab(id: string) {
    const currentLayout = editorLayoutRef.current;
    const ownerId = findEditorTabGroup(currentLayout, id);
    if (!ownerId) return;
    const owner = findEditorGroup(currentLayout, ownerId);
    const index = owner?.tabs.indexOf(id) ?? -1;
    const nextLayout = closeEditorLayoutTab(currentLayout, id, ownerId);
    const nextOwner = findEditorGroup(nextLayout, ownerId);
    const nextId = nextOwner?.activeTabId ?? null;
    const remainingReviews = reviewTabs.filter((tab) => tab.id !== id);
    reviewTabsRef.current = remainingReviews;
    setReviewTabs(remainingReviews);
    commitEditorLayout(nextLayout);
    if (owner?.activeTabId === id) {
      const nextReview = reviewTabs.find((tab) => tab.id === nextId);
      const controller = reviewNavigation;
      if (nextReview && controller) {
        controller.navigate(nextReview.state, "replace");
      } else if (index >= 0) {
        activeEditorTabRef.current = nextId;
      }
    }
  }

  function revealFileLine(file: string, line: number) {
    revealEditor({ _tag: "Line", file, line });
  }

  function revealWorkspaceFileLine(targetWorkspaceId: string, file: string, line: number) {
    if (targetWorkspaceId !== workspaceIdRef.current) layoutInteractionAtNavigationStartRef.current = layoutInteractionSequenceRef.current;
    workbench.openThen({ workspaceId: targetWorkspaceId, after: Effect.sync(() => revealFileLine(file, line)) });
  }

  // Open the file and move Monaco's cursor after its model has committed.
  function revealMatch(match: SearchMatch) {
    revealEditor({ _tag: "Match", file: match.file, line: match.line, start: match.start, end: match.end });
  }

  // Close the tab only — the file stays in the project, like a real IDE.
  function closeTab(filename: string) {
    const index = openTabs.indexOf(filename);
    if (index === -1) return;
    const closingId = fileEditorTabId(filename);
    const nextTabs = openTabs.filter((tab) => tab !== filename);
    setOpenTabs(nextTabs);
    setCurrentFile((current) =>
      current === filename ? nextTabs[index] ?? nextTabs[index - 1] : current
    );
    commitEditorLayout((current) => closeEditorLayoutTab(current, closingId));
  }

  function closeDatabaseTab(id: string) {
    setDatabaseTabs((current) => current.filter((tab) => tab.id !== id));
    commitEditorLayout((current) => closeEditorLayoutTab(current, id));
  }

  function openRuntimeTool(tool: RuntimeToolKind, groupId?: string) {
    commitEditorLayout((current) =>
      openOrFocusTab(current, `tool:${tool}`, groupId)
    );
  }

  function openResourceExplorer(
    explorer: ResourceExplorerKind,
    groupId?: string
  ) {
    commitEditorLayout((current) =>
      openOrFocusTab(current, `explorer:${explorer}`, groupId)
    );
  }

  // Navigation entries for the global palette. Registered from here because
  // opening a workbench tab needs the layout committer, which lives in App.
  const openRuntimeToolRef = useRef(openRuntimeTool);
  openRuntimeToolRef.current = openRuntimeTool;
  const openResourceExplorerRef = useRef(openResourceExplorer);
  openResourceExplorerRef.current = openResourceExplorer;
  const navigationCommands = useMemo(() => [
      {
        id: "nav.chat",
        label: "Focus Chat",
        group: "Navigation",
        keywords: ["chat", "agent", "assistant"],
        // Chat is always mounted; there is no tab to open, only focus to move.
        run: () => focusChatPaneRef.current()
      },
      {
        id: "nav.preview",
        label: "Go to Preview",
        group: "Navigation",
        keywords: ["preview", "browser"],
        run: () => openRuntimeToolRef.current("preview")
      },
      {
        id: "nav.http",
        label: "Go to HTTP",
        group: "Navigation",
        keywords: ["http", "request", "fetch"],
        run: () => openRuntimeToolRef.current("http")
      },
      {
        id: "nav.test",
        label: "Go to Tests",
        group: "Navigation",
        keywords: ["test", "tests", "run"],
        run: () => openRuntimeToolRef.current("test")
      },
      {
        id: "nav.console",
        label: "Go to Console",
        group: "Navigation",
        keywords: ["console", "logs", "output"],
        run: () => openRuntimeToolRef.current("console")
      },
      ...RESOURCE_TABS.map((tab) => ({
        id: `nav.explorer.${tab.id}`,
        label: `Go to ${tab.label}`,
        group: "Explorers",
        keywords: ["explorer", "resource", tab.id],
        run: () => openResourceExplorerRef.current(tab.id)
      }))
    ], []);
  useSurfaceCommands("workbench-navigation", navigationCommands);

  function closeFlowRecordingTab(tabId: string) {
    setRecordingTabs((current) => current.filter((tab) => tab.id !== tabId));
    commitEditorLayout((current) => closeEditorLayoutTab(current, tabId));
  }

  function closeWorkbenchTab(tabId: string) {
    if (tabId.startsWith("file:")) {
      closeTab(tabId.slice("file:".length));
      return;
    }
    if (tabId.startsWith("database-")) {
      closeDatabaseTab(tabId);
      return;
    }
    if (tabId.startsWith("workflow:")) {
      closeWorkflowTab(tabId);
      return;
    }
    if (tabId.startsWith("workflow-instances:")) {
      closeWorkflowInstancesTab(tabId);
      return;
    }
    if (tabId.startsWith("workflow-instance:")) {
      closeWorkflowInstanceDetailTab(tabId);
      return;
    }
    if (tabId.startsWith("kv:")) {
      closeKVTab(tabId);
      return;
    }
    if (tabId.startsWith("flow-recording:")) {
      closeFlowRecordingTab(tabId);
      return;
    }
    if (isReviewEditorTabId(tabId)) {
      closeReviewTab(tabId);
      return;
    }
    commitEditorLayout((current) => closeEditorLayoutTab(current, tabId));
  }

  function workbenchTabLabel(tab: WorkbenchTab): string {
    switch (tab.kind) {
      case "file":
        return tab.filename;
      case "database":
      case "workflow":
      case "workflow-instances":
      case "workflow-instance":
      case "kv":
      case "review":
        return tab.tab.title;
      case "flow-recording":
        return tab.title;
      case "tool":
        return tab.tool === "http"
          ? "HTTP"
          : tab.tool[0].toUpperCase() + tab.tool.slice(1);
      case "explorer":
        return (
          RESOURCE_TABS.find((entry) => entry.id === tab.explorer)?.label ??
          tab.explorer
        );
    }
  }

  function workbenchTabAriaLabel(tab: WorkbenchTab): string {
    if (tab.kind !== "database") return workbenchTabLabel(tab);
    return `${tab.tab.title}, ${tab.tab.target.namespace} / ${tab.tab.target.instance} / ${tab.tab.target.facet}`;
  }

  function workbenchTabIcon(tab: WorkbenchTab): React.ReactNode {
    switch (tab.kind) {
      case "file": {
        // The active file's save status rides on its tab, replacing the old
        // footer label: a spinner while persisting, an error glyph on failure.
        if (tab.filename === currentFile && saveState === "saving") {
          return (
            <CircleNotchIcon
              size={13}
              className="animate-spin text-kumo-subtle"
              aria-label="Saving"
            />
          );
        }
        if (tab.filename === currentFile && saveState === "error") {
          return (
            <WarningCircleIcon
              size={13}
              className="text-status-error"
              aria-label="Save failed"
            />
          );
        }
        return <FileTypeIcon path={tab.filename} className="h-[13px] w-[13px]" />;
      }
      case "database":
        return tab.tab.kind === "table" ? (
          <DatabaseIcon size={13} />
        ) : (
          <FileSql size={13} />
        );
      case "workflow":
        return <FlowArrow size={13} />;
      case "workflow-instances":
        return <ListBullets size={13} />;
      case "workflow-instance":
        return <FlowArrow size={13} weight="fill" />;
      case "kv":
        return <Key size={13} />;
      case "review":
        return <ArrowsLeftRight size={13} />;
      case "tool":
        if (tab.tool === "preview") return <Eye size={14} />;
        if (tab.tool === "http") return <ArrowsLeftRight size={14} />;
        if (tab.tool === "test") return <Flask size={14} />;
        return <TerminalWindow size={14} />;
      case "explorer":
        return (
          RESOURCE_TABS.find((entry) => entry.id === tab.explorer)?.icon ?? null
        );
    }
  }

  function beginRenameFile(filename: string) {
    setTreeMenu(null);
    setRenameFileSource(filename);
    setRenameFileName(filename);
    setRenameFileOpen(true);
  }

  function closeRenameFile() {
    setRenameFileOpen(false);
    setRenameFileSource(null);
    setRenameFileName("");
  }

  function handleRenameFile() {
    const source = renameFileSource;
    if (!source || !(source in files)) return;
    const target = renameFileName
      .trim()
      .replace(/\\/g, "/")
      .replace(/\/{2,}/g, "/");
    if (!target || target.startsWith("/") || target.endsWith("/")) {
      window.alert("Enter a relative file path");
      return;
    }
    const segments = target.split("/");
    if (
      segments.some(
        (segment) => !segment || segment === "." || segment === ".."
      )
    ) {
      window.alert("Invalid file name");
      return;
    }
    if (target === source) {
      closeRenameFile();
      return;
    }
    const ancestors = segments
      .slice(0, -1)
      .map((_segment, index) => segments.slice(0, index + 1).join("/"));
    const conflicts =
      target in files ||
      folders.includes(target) ||
      ancestors.some((path) => path in files) ||
      Object.keys(files).some(
        (path) => path !== source && path.startsWith(`${target}/`)
      );
    if (conflicts) {
      window.alert("A file or folder already exists at that path");
      return;
    }

    setFiles((current) =>
      Object.fromEntries(
        Object.entries(current).map(([path, contents]) =>
          path === source ? [target, contents] : [path, contents]
        )
      )
    );
    setOpenTabs((current) =>
      current.map((filename) => (filename === source ? target : filename))
    );
    const sourceTabId = fileEditorTabId(source);
    const targetTabId = fileEditorTabId(target);
    const currentLayout = editorLayoutRef.current;
    const ownerId = findEditorTabGroup(currentLayout, sourceTabId);
    const owner = ownerId ? findEditorGroup(currentLayout, ownerId) : undefined;
    if (ownerId && owner) {
      const index = owner.tabs.indexOf(sourceTabId);
      const previousActive = owner.activeTabId;
      let nextLayout = closeEditorLayoutTab(
        currentLayout,
        sourceTabId,
        ownerId
      );
      nextLayout = openOrFocusTab(nextLayout, targetTabId, ownerId);
      nextLayout = reorderEditorLayoutTab(
        nextLayout,
        ownerId,
        targetTabId,
        index
      );
      if (previousActive && previousActive !== sourceTabId) {
        nextLayout = selectEditorLayoutTab(
          nextLayout,
          ownerId,
          previousActive
        );
      }
      commitEditorLayout(nextLayout);
    }
    setCurrentFile((current) => (current === source ? target : current));
    setCollapsedFolders((current) => {
      const next = new Set(current);
      for (const ancestor of ancestors) next.delete(ancestor);
      return next;
    });
    closeRenameFile();
  }

  function handleAddFile() {
    const filename = addFileName.trim();
    if (!filename) return;
    if (files[filename]) {
      window.alert("File already exists");
      return;
    }

    setFiles((prev) => ({
      ...prev,
      [filename]: filename.endsWith(".json") ? "{}" : ""
    }));
    openFile(filename);
    setAddFileName("");
    setAddFileOpen(false);
  }

  function createStarterTest(kind: "runtime" | "e2e") {
    const baseName =
      kind === "runtime" ? "test/worker.test.ts" : "test/worker.e2e.test.ts";
    let filename = baseName;
    let index = 2;
    while (files[filename] !== undefined) {
      filename = baseName.replace(/\.test\.ts$/, `-${index++}.test.ts`);
    }

    const entryPoint = workerEntryFromFiles(files);
    const source =
      kind === "runtime"
        ? `import { exports } from "cloudflare:workers";
import { describe, expect, it } from "vitest";

describe("Worker runtime", () => {
  it("responds successfully", async () => {
    const response = await exports.default.fetch("https://example.com/");

    expect(response.status).toBe(200);
  });
});
`
        : `import { afterAll, afterEach, beforeAll, describe, expect, it } from "vitest";
import { createTestHarness } from "wrangler";

const server = createTestHarness({
  workers: [
    {
      config: {
        name: ${JSON.stringify(workerNameFromFiles(files))},
        main: ${JSON.stringify(entryPoint)},
        compatibility_date: "2026-06-11"
      }
    }
  ]
});

beforeAll(() => server.listen());
afterEach(() => server.reset());
afterAll(() => server.close());

describe("Worker E2E", () => {
  it("serves a request from the production-style build", async () => {
    const response = await server.fetch("/");

    expect(response.status).toBe(200);
  });
});
`;

    setFiles((current) => ({ ...current, [filename]: source }));
    setFolders((current) =>
      current.includes("test") ? current : [...current, "test"]
    );
    openFile(filename);
    setConsoleSource("test");
    commitEditorLayout((current) => openOrFocusTab(current, "tool:test"));
  }

  function createFolder() {
    const cleaned = newFolderName
      .trim()
      .replace(/^\/+|\/+$/g, "")
      .replace(/\/{2,}/g, "/");
    if (!cleaned) return;
    if (cleaned.split("/").some((seg) => seg === "" || seg === "..")) {
      window.alert("Invalid folder name");
      return;
    }
    setFolders((prev) => (prev.includes(cleaned) ? prev : [...prev, cleaned]));
    setCollapsedFolders((prev) => {
      // Make sure the new folder's ancestors are expanded so it is visible.
      const next = new Set(prev);
      const segments = cleaned.split("/");
      for (let i = 1; i <= segments.length; i++) {
        next.delete(segments.slice(0, i).join("/"));
      }
      return next;
    });
    setNewFolderName("");
    setNewFolderOpen(false);
  }

  // Delete a folder: every file under it plus its explicit folder entries.
  function removeFolder(folderPath: string) {
    const prefix = `${folderPath}/`;
    const contained = Object.keys(files).filter((path) =>
      path.startsWith(prefix)
    );
    if (contained.length === Object.keys(files).length) {
      window.alert("Cannot delete the folder containing all files");
      return;
    }

    if (contained.length > 0) {
      const nextFiles = { ...files };
      for (const path of contained) delete nextFiles[path];
      setFiles(nextFiles);
      const nextTabs = openTabs.filter((tab) => !tab.startsWith(prefix));
      setOpenTabs(nextTabs);
      let nextLayout = editorLayoutRef.current;
      for (const path of contained) {
        nextLayout = closeEditorLayoutTab(
          nextLayout,
          fileEditorTabId(path)
        );
      }
      commitEditorLayout(nextLayout);
      if (currentFile && currentFile.startsWith(prefix)) {
        setCurrentFile(nextTabs[0]);
      }
    }
    setFolders((prev) =>
      prev.filter(
        (folder) => folder !== folderPath && !folder.startsWith(prefix)
      )
    );
  }

  // Delete the file from the project (explorer action) and close its tab.
  function removeFile(filename: string) {
    if (Object.keys(files).length <= 1) {
      window.alert("Cannot delete the last file");
      return;
    }

    const nextFiles = { ...files };
    delete nextFiles[filename];
    setFiles(nextFiles);
    closeTab(filename);
  }

  function formatGroupFile(groupId: string) {
    const group = findEditorGroup(editorLayoutRef.current, groupId);
    const tabId = group?.activeTabId;
    if (!tabId?.startsWith("file:")) return;
    void editorRefs.current
      .get(tabId)
      ?.getAction("editor.action.formatDocument")
      ?.run();
  }

  function runWorker(init?: RunRequestInit) { execution.dispatch({ _tag: "Preview", ...(init === undefined ? {} : { init }) }); }
  function runTests() { execution.dispatch({ _tag: "Tests" }); }

  function sendHttpRequest() {
    const hasBody = method !== "GET" && method !== "HEAD";
    void runWorker({
      method,
      path,
      headers: reqHeaders.filter(([name]) => name.trim().length > 0),
      body: hasBody ? reqBody : null
    });
  }

  function renderWorkbenchTab(
    descriptor: WorkbenchTab,
    _groupId: string,
    active: boolean,
    focused: boolean
  ): React.ReactNode {
    switch (descriptor.kind) {
      case "file": {
        const tabId = descriptor.id;
        return (
          <div className="flex h-full min-h-0 flex-col">
            <CodeEditor
              key={activeWorkspace ?? "unhydrated-workspace"}
              currentFile={descriptor.filename}
              viewStates={editorViewStates.current}
              viewStateScope={editorViewStateScope.current}
              onChange={(filename, contents) => {
                // Invalidate an in-flight reload synchronously. The autosave
                // effect runs after React commits, leaving a small window in
                // which an older response could otherwise win.
                if (filename in files && files[filename] !== contents) {
                  workbench.invalidateReload(undefined);
                }
                setFiles((current) =>
                  filename in current && current[filename] !== contents
                    ? { ...current, [filename]: contents }
                    : current
                );
              }}
              onMount={(editor) => {
                editorRefs.current.set(tabId, editor);
              }}
              onUnmount={(editor) => {
                if (editorRefs.current.get(tabId) === editor) {
                  editorRefs.current.delete(tabId);
                }
              }}
            />
          </div>
        );
      }
      case "flow-recording":
        return (
          <RecordingPlayerPane
            workspaceId={descriptor.scope}
            recordingId={descriptor.recordingId}
            live={false}
            onBack={() => closeFlowRecordingTab(descriptor.id)}
            onDeleted={() => closeFlowRecordingTab(descriptor.id)}
          />
        );
      case "database": {
        const tab = descriptor.tab;
        return (
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center text-xs text-kumo-subtle">
                Loading database editor…
              </div>
            }
          >
            <StudioEditorSurface
              workspaceId={tab.target.workspaceId}
              kind={tab.target.kind ?? "durable-object"}
              namespace={tab.target.namespace}
              instance={tab.target.instance}
              facet={tab.target.facet}
              view={tab.kind}
              title={tab.title}
              active={active && focused}
              onTitleChange={(title) =>
                setDatabaseTabs((current) =>
                  current.map((entry) =>
                    entry.id === tab.id ? { ...entry, title } : entry
                  )
                )
              }
              schemaName={tab.schemaName}
              tableName={tab.tableName}
            />
          </Suspense>
        );
      }
      case "workflow": {
        const tab = descriptor.tab;
        return (
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center text-xs text-kumo-subtle">
                Loading Workflow visualization…
              </div>
            }
          >
            <WorkflowVisualization
              workspaceId={tab.workspaceId}
              definition={tab.definition}
              initialInstance={tab.instance}
              active={active}
              onOpenSource={(file, line) =>
                void revealWorkspaceFileLine(tab.workspaceId, file, line)
              }
            />
          </Suspense>
        );
      }
      case "workflow-instances": {
        const tab = descriptor.tab;
        return (
          <WorkflowInstancesSurface
            workspaceId={tab.workspaceId}
            definition={tab.definition}
            active={active && focused}
            onOpenInstance={(instance) =>
              openWorkflowInstanceDetail(
                tab.definition,
                instance.id,
                tab.workspaceId
              )
            }
          />
        );
      }
      case "workflow-instance": {
        const tab = descriptor.tab;
        return (
          <WorkflowInstanceSurface
            workspaceId={tab.workspaceId}
            definition={tab.definition}
            instanceId={tab.instanceId}
            active={active && focused}
            onOpenSource={(file, line) =>
              void revealWorkspaceFileLine(tab.workspaceId, file, line)
            }
            onOpenGraph={() =>
              openWorkflow(tab.definition, undefined, tab.workspaceId)
            }
          />
        );
      }
      case "kv": {
        const tab = descriptor.tab;
        return (
          <KVBrowseSurface
            workspaceId={tab.workspaceId}
            bindingName={tab.bindingName}
            namespace={tab.namespace}
          />
        );
      }
      case "review":
        return (
          <ReviewWorkbench
            state={descriptor.tab.state}
            onNavigate={navigateReview}
          />
        );
      case "tool":
        if (descriptor.tool === "preview") {
          return (
            <PreviewSurface
              result={result}
              error={error}
              path={path}
              workerUrlBase={workerUrlBase}
              runId={runId}
              running={running}
              onPathChange={setPath}
              onRun={() => void runWorker({ path })}
            />
          );
        }
        if (descriptor.tool === "http") {
          return (
            <HttpSurface
              result={result}
              error={error}
              path={path}
              workerUrlBase={workerUrlBase}
              running={running}
              request={{ method, headers: reqHeaders, body: reqBody }}
              onPathChange={setPath}
              onRequestChange={(request) => {
                setMethod(request.method);
                setReqHeaders(request.headers);
                setReqBody(request.body);
              }}
              onSend={sendHttpRequest}
            />
          );
        }
        if (descriptor.tool === "test") {
          return (
            <TestSurface
              files={files}
              result={testResult}
              error={testError}
              running={testing}
              watch={testWatch}
              inFileConcurrency={inFileConcurrency}
              onWatchChange={(enabled) => execution.dispatch({ _tag: "WatchTests", enabled })}
              onInFileConcurrencyChange={setInFileConcurrency}
              onRun={() => void runTests()}
              onCreate={createStarterTest}
              onOpenFile={openFile}
              onOpenRecording={(recordingId, recordingWorkspaceId) =>
                window.dispatchEvent(
                  new CustomEvent(OPEN_FLOW_RECORDING_EVENT, {
                    detail: { recordingId, workspaceId: recordingWorkspaceId }
                  })
                )
              }
            />
          );
        }
        return (
          <ConsoleSurface
            source={consoleSource}
            result={result}
            error={error}
            testResult={testResult}
            testError={testError}
            contextLabel={
              consoleSource === "test"
                ? `Tests · ${activeWorkerName}`
                : `Worker · ${activeWorkerName}`
            }
          />
        );
      case "explorer":
        // The sidebar already holds this explorer's tree; rendering it here
        // too showed the same tree twice. The pane belongs to whatever the
        // tree opens.
        return (
          <p className="p-4 text-[12px] text-kumo-subtle">
            Choose an item in the{" "}
            {RESOURCE_TABS.find((entry) => entry.id === descriptor.explorer)
              ?.label ?? descriptor.explorer}{" "}
            explorer to open it here.
          </p>
        );
    }
  }

  // Resource explorers render full-height inside an editor tab. The sidebar
  // collapse chrome is inert here — the tab itself is the visibility control.
  // headerLeading is an empty node so each explorer's caret + section-name
  // toggle is suppressed (the tab already names the section) with no spacer,
  // which left-aligns the header actions (e.g. Refresh).
  function renderResourceExplorer(
    explorer: ResourceExplorerKind
  ): React.ReactNode {
    const chrome = {
      collapsed: false,
      onCollapsedChange: () => {},
      headerLeading: <></>
    };
    switch (explorer) {
      case "databases":
        return (
          <DatabaseExplorer
            {...chrome}
            workspaceId={activeWorkspace}
            connectionName={activeWorkerName}
            onOpenQuery={openDatabaseQuery}
            onOpenTable={openDatabaseTable}
          />
        );
      case "workflows":
        return (
          <WorkflowExplorer
            {...chrome}
            workspaceId={activeWorkspace}
            connectionName={activeWorkerName}
            refreshToken={runId}
            onOpenWorkflow={openWorkflow}
            onOpenInstances={openWorkflowInstances}
            onOpenInstance={(definition, instance) =>
              openWorkflowInstanceDetail(definition, instance.id)
            }
          />
        );
      case "containers":
        return (
          <ContainerExplorer
            {...chrome}
            workspaceId={activeWorkspace}
            connectionName={activeWorkerName}
            refreshToken={runId}
          />
        );
      case "r2":
        return (
          <R2Explorer
            {...chrome}
            workspaceId={activeWorkspace}
            connectionName={activeWorkerName}
            refreshToken={runId}
          />
        );
      case "kv":
        return (
          <KVExplorer
            {...chrome}
            workspaceId={activeWorkspace}
            connectionName={activeWorkerName}
            refreshToken={runId}
            onOpenBrowse={openKVBrowse}
          />
        );
      case "queues":
        return (
          <QueueExplorer
            {...chrome}
            workspaceId={activeWorkspace}
            connectionName={activeWorkerName}
            refreshToken={runId}
          />
        );
      case "workers":
        return (
          <WorkerBindingsExplorer
            {...chrome}
            workspaceId={activeWorkspace}
            connectionName={activeWorkerName}
            refreshToken={runId}
          />
        );
      case "browser":
        return (
          <BrowserObservabilityExplorer
            {...chrome}
            stationId={sessionIdRef.current}
            workspaceId={activeWorkspace}
            connectionName={activeWorkerName}
            refreshToken={runId}
          />
        );
      case "flow-recordings":
        return (
          <FlowRecordingsExplorer
            {...chrome}
            workspaceId={activeWorkspace}
            connectionName={activeWorkerName}
            refreshToken={runId}
            initialRecordingId={flowRecordingDeepLink?.recordingId}
            initialScope={flowRecordingDeepLink?.workspaceId}
            onOpenRecording={openFlowRecordingTab}
          />
        );
      case "history":
        return (
          <HistoryExplorer
            {...chrome}
            stationId={sessionIdRef.current}
            workspaceId={activeWorkspace}
            connectionName={activeWorkerName}
            refreshToken={runId}
            onFlushPending={() => workbench.flush}
          />
        );
      case "traces":
        return (
          <TraceExplorer
            {...chrome}
            stationId={sessionIdRef.current}
            workspaceId={activeWorkspace}
            connectionName={activeWorkerName}
            refreshToken={runId}
          />
        );
    }
  }

  const activeWorkerName = useMemo(() => workerNameFromFiles(files), [files]);
  const editorStationForWorker = !workerstationBasePath()
    ? stationIdFromHostname(window.location.hostname)
    : null;
  // On the editor subdomain the worker gets its OWN subdomain
  // (<worker>--<station>.apex, served at root). Fall back to the path form for
  // names that cannot fit in a DNS label and for nested/localhost.
  const workerUrlBase =
    editorStationForWorker
      ? workerPreviewBase(activeWorkerName, editorStationForWorker, window.location.protocol)
      : `${window.location.origin}${workerstationBasePath()}/${sessionIdRef.current ?? ""}/${activeWorkerName}`;

  if (!booted) {
    return <div className="fixed inset-0 bg-kumo-base" />;
  }

  if (bootstrapError) {
    return (
      <OpencodeThemeProvider>
        <div
          className="oc-skin fixed inset-0 flex items-center justify-center bg-kumo-base p-6"
          data-theme-mode="light"
        >
          <div
            role="alert"
            data-component="bootstrap-error"
            className="flex max-w-md flex-col items-center gap-3 text-center"
          >
            <WarningCircleIcon size={28} className="text-status-error" />
            <h1 className="text-base font-medium text-kumo-default">
              Workerstation could not start
            </h1>
            <p className="text-sm text-kumo-subtle">{bootstrapError}</p>
            <button
              type="button"
              className="rounded bg-kumo-contrast px-3 py-1.5 text-sm font-medium text-kumo-base"
              onClick={() => window.location.reload()}
            >
              Reload
            </button>
          </div>
        </div>
      </OpencodeThemeProvider>
    );
  }

  // The apex root: no station is open, so the workbench has nothing to render.
  if (showLauncher) {
    return (
      <OpencodeThemeProvider>
        <div
          className="oc-skin fixed inset-0"
          data-component="shell"
          data-theme-mode="light"
        >
          <StationLauncher />
        </div>
      </OpencodeThemeProvider>
    );
  }

  return (
    <OpencodeThemeProvider>
    <KumoPortalProvider container={kumoPortalContainer}>
    <div
      className="oc-skin fixed inset-0 flex flex-col"
      data-component="shell"
      data-theme-mode="light"
    >
      {/* Title bar: the station and its workerspaces as tabs. */}
      <div data-component="shell-titlebar">
        <div
          role="tablist"
          aria-label="Workerspaces"
          data-component="shell-tabs"
        >
        {workspaceList.map((id, index) => (
          <button
            key={id}
            type="button"
            title={`Workerspace ${index + 1} — ${id}`}
            draggable
            onDragStart={(e) => {
              dragIndexRef.current = index;
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("text/plain", id);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              setDragOverIndex(index);
            }}
            onDragLeave={() => setDragOverIndex(null)}
            onDrop={(e) => {
              e.preventDefault();
              const from = dragIndexRef.current;
              setDragOverIndex(null);
              dragIndexRef.current = null;
              if (from === null || from === index) return;
              const movedId = workspaceList[from];
              const targetId = workspaceList[index];
              if (!movedId || !targetId) return;
              workbench.dispatch({ _tag: "Reorder", movedId, targetId, placeAfterTarget: from < index });
            }}
            onDragEnd={() => {
              dragIndexRef.current = null;
              setDragOverIndex(null);
            }}
            onClick={() => void openWorkspace(id)}
            onContextMenu={(e) => {
              e.preventDefault();
              setRailMenu({ x: e.clientX, y: e.clientY, id });
            }}
            role="tab"
            aria-selected={id === activeWorkspace}
            data-component="shell-tab"
            data-dragover={dragOverIndex === index ? "true" : undefined}
          >
            <span data-slot="label">{id}</span>
          </button>
        ))}
        {/* Inside the tab list so it trails the last workerspace instead of
            sitting at the far end of the bar. */}
        <button
          type="button"
          aria-label="New workerspace"
          title="New workerspace"
          onClick={() => void addWorkspace()}
          data-component="shell-icon-button"
        >
          <Plus size={13} weight="bold" />
        </button>
        </div>
      </div>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="relative min-h-0 flex-1">
          <div data-component="shell-panes">
            {/* Chat is a pane of the shell, not an editor tab: as a tab it was
                closable, reorderable, and dropped by every workspace
                rehydrate. A pane has no tab lifecycle to get wrong. */}
            <div
              ref={chatPaneRef}
              data-component="shell-chat-pane"
              style={{ width: "24rem" }}
            >
              <ChatSurface
                stationId={sessionIdRef.current ?? ""}
                workspaceId={activeWorkspace}
                onWorkspaceInvalidated={() => {
                  void reloadActiveWorkspace();
                  setChangesToken((token) => token + 1);
                }}
              />
            </div>
            <div data-component="shell-right-pane">
              <div
                role="tablist"
                aria-label="Workspace panes"
                data-component="pane-tabs"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={rightPaneTab === "changes"}
                  onClick={() => setRightPaneTab("changes")}
                  data-component="pane-tab"
                  aria-label="Files changed"
                  title="Files changed"
                >
                  <svg
                    viewBox="0 0 16 16"
                    width="14"
                    height="14"
                    fill="currentColor"
                    aria-hidden="true"
                    focusable="false"
                    className="shrink-0 overflow-visible"
                  >
                    <path d="M1.5 3.25a2.25 2.25 0 1 1 3 2.122v5.256a2.251 2.251 0 1 1-1.5 0V5.372A2.25 2.25 0 0 1 1.5 3.25Zm5.677-.177L9.573.677A.25.25 0 0 1 10 .854V2.5h1A2.5 2.5 0 0 1 13.5 5v5.628a2.251 2.251 0 1 1-1.5 0V5a1 1 0 0 0-1-1h-1v1.646a.25.25 0 0 1-.427.177L7.177 3.427a.25.25 0 0 1 0-.354ZM3.75 2.5a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm0 9.5a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm8.25.75a.75.75 0 1 0 1.5 0 .75.75 0 0 0-1.5 0Z" />
                  </svg>
                  {changedCount === null ? null : (
                    <span data-slot="count">{changedCount}</span>
                  )}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={rightPaneTab === "files" && !toolSurfaceActive}
                  onClick={() => {
                    setRightPaneTab("files");
                    // Prefer the focused group's files scope, then another
                    // group's. Closing a group's last file can leave a hidden
                    // tool active there while an open file remains elsewhere.
                    const current = editorLayoutRef.current;
                    const groups = editorGroups(current);
                    const focused = groups.find(
                      (group) => group.id === current.focusedGroupId
                    );
                    const target = [
                      ...(focused ? [focused] : []),
                      ...groups.filter((group) => group.id !== focused?.id)
                    ]
                      .flatMap((group) => group.tabs)
                      .find((tabId) => tabScope(tabId) === FILES_SCOPE);
                    if (target) {
                      commitEditorLayout(selectEditorTab(current, target));
                    } else {
                      // With every file tab closed, reopen the workspace's
                      // primary source so the project tree is reachable again.
                      const primary = inferPrimaryFile(files);
                      if (primary) openFile(primary);
                    }
                  }}
                  data-component="pane-tab"
                  aria-label="Open file"
                  title="Open file"
                >
                  <svg
                    viewBox="0 0 16 16"
                    width="14"
                    height="14"
                    fill="currentColor"
                    aria-hidden="true"
                    focusable="false"
                    className="shrink-0"
                  >
                    <path d="M2 1.75C2 .784 2.784 0 3.75 0h6.586c.464 0 .909.184 1.237.513l2.914 2.914c.329.328.513.773.513 1.237v9.586A1.75 1.75 0 0 1 13.25 16h-9.5A1.75 1.75 0 0 1 2 14.25Zm1.75-.25a.25.25 0 0 0-.25.25v12.5c0 .138.112.25.25.25h9.5a.25.25 0 0 0 .25-.25V6h-2.75A1.75 1.75 0 0 1 9 4.25V1.5Zm6.75.062V4.25c0 .138.112.25.25.25h2.688l-.011-.013-2.914-2.914-.013-.011Z" />
                  </svg>
                </button>
                {/* The view tools live in this bar rather than as icons in the
                    editor chrome, so every surface is reached the same way. */}
                <span data-slot="divider" aria-hidden="true" />
                {RUNTIME_TOOL_TABS.map((tool) => (
                  <button
                    key={tool.id}
                    type="button"
                    role="tab"
                    aria-selected={rightPaneTab === "files" && activeScope === tool.id}
                    onClick={() => {
                      setRightPaneTab("files");
                      if (activeToolTab === tool.id) closeWorkbenchTab(tool.id);
                      else openRuntimeTool(tool.tool);
                    }}
                    data-component="pane-tab"
                    data-collapsible="true"
                    title={
                      tool.tool === "http"
                        ? "HTTP"
                        : tool.tool[0].toUpperCase() + tool.tool.slice(1)
                    }
                  >
                    <span data-slot="icon" aria-hidden="true">
                      {RUNTIME_TOOL_ICONS[tool.tool]}
                    </span>
                    <span data-slot="label">
                      {tool.tool === "http"
                        ? "HTTP"
                        : tool.tool[0].toUpperCase() + tool.tool.slice(1)}
                    </span>
                  </button>
                ))}
                <span data-slot="divider" aria-hidden="true" />
                {RESOURCE_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={
                      rightPaneTab === "files" &&
                      activeScope === `explorer:${tab.id}`
                    }
                    onClick={() => {
                      setRightPaneTab("files");
                      if (activeToolTab === `explorer:${tab.id}`)
                        closeWorkbenchTab(`explorer:${tab.id}`);
                      else openResourceExplorer(tab.id);
                    }}
                    data-component="pane-tab"
                    data-collapsible="true"
                    title={tab.label}
                  >
                    <span data-slot="icon" aria-hidden="true">
                      {EXPLORER_GLYPHS[tab.id] ? (
                        <GlyphLabel text={EXPLORER_GLYPHS[tab.id]} />
                      ) : (
                        tab.icon
                      )}
                    </span>
                    <span data-slot="label">{tab.label}</span>
                  </button>
                ))}
              </div>
              {/* Both panes stay mounted: unmounting the changes tab would
                  discard its selection and refetch on every switch. */}
              <div
                className={`min-h-0 flex-1 ${rightPaneTab === "changes" ? "" : "hidden"}`}
              >
                <WorkspaceChangesView
                  workspaceId={activeWorkspace}
                  files={files}
                  refreshToken={changesToken}
                  model={workspaceChanges}
                />
              </div>
              <div
                className={`flex min-h-0 min-w-0 flex-1 ${rightPaneTab === "files" ? "" : "hidden"}`}
              >
            <SplitPane
              direction="vertical"
              defaultRatio={17}
              defaultFirstPaneSize={204}
              min={12}
              max={40}
              collapsible="first"
            >
            {/* The sidebar shows the explorer belonging to the active scope:
                the project tree for files, otherwise that tool's own. Keeping
                the tool's tree here means opening a result no longer replaces
                the thing you are browsing. */}
            {scopeExplorer ? (
              <div className="flex h-full w-full flex-col">
                {renderResourceExplorer(scopeExplorer)}
              </div>
            ) : (
            <div className="flex h-full w-full flex-col">
              <div className="min-h-0 flex-1">
                <section
                  className="flex h-full min-h-0 flex-col"
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setTreeMenu({ x: e.clientX, y: e.clientY });
                  }}
                >
                  <div className="min-h-0 flex-1 overflow-auto py-1">
                    <FileTreeRows
                      nodes={fileTree}
                      depth={0}
                      collapsed={collapsedFolders}
                      onToggle={toggleFolder}
                      currentFile={
                        activeEditorTab?.startsWith("file:")
                          ? activeEditorTab.slice("file:".length)
                          : undefined
                      }
                      onOpen={openFile}
                      onDelete={removeFile}
                      onRename={beginRenameFile}
                      onFileContextMenu={(path, x, y) =>
                        setTreeMenu({ x, y, file: path })
                      }
                      onFolderContextMenu={(path, x, y) =>
                        setTreeMenu({ x, y, folder: path })
                      }
                    />
                  </div>
                </section>

              </div>
            </div>
            )}

            {/* Grouped workbench: every surface is placed by the recursive layout. */}
            <EditorLayout
              node={editorLayout.root}
              tabs={workbenchTabs}
              // Each surface gets its own tab set: a group shows only the
              // tabs belonging to whatever it is currently displaying, so a
              // database result never sits beside a KV browser or a file.
              hideTabFromStrip={(tabId, groupActiveTabId) => {
                const scope = activeScopeOf(groupActiveTabId);
                // The tool's own tab is represented in the pane bar, so it
                // would otherwise appear twice.
                if (tabId === scope) return true;
                return !isTabInScope(tabId, scope);
              }}
              getTabId={(tab) => tab.id}
              getTabLabel={(tab) => workbenchTabLabel(tab)}
              getTabAriaLabel={(tab) => workbenchTabAriaLabel(tab)}
              getTabIcon={(tab) => workbenchTabIcon(tab)}
              focusedGroupId={editorLayout.focusedGroupId}
              onGroupFocus={focusWorkbenchGroup}
              onSelectTab={(tabId, groupId) => {
                const review = reviewTabs.find((tab) => tab.id === tabId);
                if (review) activateReviewTab(review, groupId);
                else {
                  commitEditorLayout((current) =>
                    selectEditorLayoutTab(current, groupId, tabId)
                  );
                }
              }}
              onCloseTab={(tabId) => closeWorkbenchTab(tabId)}
              onSplit={(groupId, direction) => {
                const current = editorLayoutRef.current;
                const next = splitEditorLayoutGroup(
                  current,
                  groupId,
                  direction
                );
                commitEditorLayout(focusEditorGroup(next, groupId));
              }}
              onSplitRatioChange={(splitId, ratio) =>
                commitEditorLayout((current) =>
                  setSplitRatio(current, splitId, ratio)
                )
              }
              onCloseGroup={(groupId) => {
                const current = editorLayoutRef.current;
                const nextGroupId = findAdjacentGroup(current, groupId);
                let next = collapseEmptyGroup(current, groupId);
                if (nextGroupId && findEditorGroup(next, nextGroupId)) {
                  next = focusEditorGroup(next, nextGroupId);
                }
                commitEditorLayout(next);
              }}
              renderMenuItems={({ groupId, close }) => {
                const groupActiveTab = findEditorGroup(
                  editorLayoutRef.current,
                  groupId
                )?.activeTabId;
                const toggleTheme = () => {
                  const next =
                    (document.documentElement.getAttribute("data-mode") ??
                      "light") === "dark"
                      ? "light"
                      : "dark";
                  document.documentElement.setAttribute("data-mode", next);
                  document.documentElement.classList.toggle("dark", next === "dark");
                  document.documentElement.style.colorScheme = next;
                  localStorage.setItem("theme", next);
                };
                return (
                  <>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setSearchOpen(true);
                        close();
                      }}
                      className={MENU_ITEM_CLASS}
                    >
                      <MagnifyingGlassIcon size={14} aria-hidden="true" /> Search files
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        toggleTheme();
                        close();
                      }}
                      className={MENU_ITEM_CLASS}
                    >
                      <MoonIcon size={14} aria-hidden="true" /> Toggle theme
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      disabled={!groupActiveTab?.startsWith("file:")}
                      onClick={() => {
                        formatGroupFile(groupId);
                        close();
                      }}
                      className={MENU_ITEM_CLASS}
                    >
                      <BracketsCurlyIcon size={14} aria-hidden="true" /> Format code
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      aria-pressed={watchMode}
                      onClick={() => {
                        execution.dispatch({ _tag: "WatchPreview", enabled: !watchMode });
                        close();
                      }}
                      className={MENU_ITEM_CLASS}
                    >
                      <ArrowsClockwiseIcon size={14} aria-hidden="true" /> Watch mode:{" "}
                      {watchMode ? "on" : "off"}
                    </button>
                  </>
                );
              }}
              onTabReorder={({ groupId, tabId, toIndex }) =>
                commitEditorLayout((current) =>
                  reorderEditorLayoutTab(current, groupId, tabId, toIndex)
                )
              }
              onMoveTabToAdjacentGroup={({ tabId, targetGroupId }) =>
                moveWorkbenchTab(tabId, targetGroupId)
              }
              onTabDrop={(payload, target) => {
                if (payload.tabId === target.tabId) return;
                const current = editorLayoutRef.current;
                if (
                  findEditorTabGroup(current, payload.tabId) !==
                  payload.sourceGroupId
                ) {
                  return;
                }
                const targetGroup = findEditorGroup(current, target.groupId);
                if (!targetGroup) return;
                const withoutDragged = targetGroup.tabs.filter(
                  (id) => id !== payload.tabId
                );
                const targetIndex = target.tabId
                  ? Math.max(0, withoutDragged.indexOf(target.tabId)) +
                    (target.position === "after" ? 1 : 0)
                  : withoutDragged.length;
                moveWorkbenchTab(
                  payload.tabId,
                  target.groupId,
                  targetIndex
                );
              }}
              renderTab={(tab, context) =>
                renderWorkbenchTab(
                  tab,
                  context.groupId,
                  context.active,
                  context.focused
                )
              }
              className="min-w-0 flex-1"
            />
            </SplitPane>
          </div>
            </div>
            </div>
        </div>
      </div>

      {/* Workspace rail context menu */}
      {railMenu
        ? createPortal(
            <div
              className="fixed inset-0 z-[9998]"
              onClick={() => setRailMenu(null)}
              onContextMenu={(e) => {
                e.preventDefault();
                setRailMenu(null);
              }}
            >
              <div
                style={{
                  left: Math.min(railMenu.x, window.innerWidth - 220),
                  top: Math.min(railMenu.y, window.innerHeight - 80)
                }}
                className="fixed z-[9999] min-w-48 rounded-lg border border-kumo-line bg-kumo-base py-1 shadow-lg"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => {
                    const target = railMenu.id;
                    setRailMenu(null);
                    void deleteWorkspace(target);
                  }}
                  className="flex w-full cursor-pointer items-center gap-2 bg-transparent px-3 py-1.5 text-left text-[13px] text-status-error hover:bg-kumo-elevated"
                >
                  <X size={13} />
                  Delete workerspace {workspaceList.indexOf(railMenu.id) + 1}
                </button>
              </div>
            </div>,
            document.body
          )
        : null}

      {/* File tree context menu */}
      {treeMenu
        ? createPortal(
            <div
              className="fixed inset-0 z-[9998]"
              onClick={() => setTreeMenu(null)}
              onContextMenu={(e) => {
                e.preventDefault();
                setTreeMenu(null);
              }}
            >
              <div
                style={{
                  left: Math.min(treeMenu.x, window.innerWidth - 200),
                  top: Math.min(treeMenu.y, window.innerHeight - 140)
                }}
                className="fixed z-[9999] min-w-44 rounded-lg border border-kumo-line bg-kumo-base py-1 shadow-lg"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => {
                    const dir = treeMenu.folder;
                    setTreeMenu(null);
                    setAddFileName(dir ? `${dir}/` : "");
                    setAddFileOpen(true);
                  }}
                  className="flex w-full cursor-pointer items-center gap-2 bg-transparent px-3 py-1.5 text-left text-[13px] text-kumo-default hover:bg-kumo-elevated"
                >
                  <Plus size={13} weight="bold" />
                  New file…
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const dir = treeMenu.folder;
                    setTreeMenu(null);
                    setNewFolderName(dir ? `${dir}/` : "");
                    setNewFolderOpen(true);
                  }}
                  className="flex w-full cursor-pointer items-center gap-2 bg-transparent px-3 py-1.5 text-left text-[13px] text-kumo-default hover:bg-kumo-elevated"
                >
                  <FolderIcon size={13} />
                  New folder…
                </button>
                {treeMenu.file ? (
                  <button
                    type="button"
                    onClick={() => {
                      const target = treeMenu.file;
                      if (target) beginRenameFile(target);
                    }}
                    className="flex w-full cursor-pointer items-center gap-2 bg-transparent px-3 py-1.5 text-left text-[13px] text-kumo-default hover:bg-kumo-elevated"
                  >
                    <PencilSimple size={13} />
                    Rename {treeMenu.file.split("/").pop()}…
                  </button>
                ) : null}
                {treeMenu.file && treeMenu.file !== "package.json" ? (
                  <button
                    type="button"
                    onClick={() => {
                      const target = treeMenu.file;
                      setTreeMenu(null);
                      if (target) removeFile(target);
                    }}
                    className="flex w-full cursor-pointer items-center gap-2 bg-transparent px-3 py-1.5 text-left text-[13px] text-status-error hover:bg-kumo-elevated"
                  >
                    <X size={13} />
                    Delete {treeMenu.file.split("/").pop()}
                  </button>
                ) : null}
                {treeMenu.folder ? (
                  <button
                    type="button"
                    onClick={() => {
                      const target = treeMenu.folder;
                      setTreeMenu(null);
                      if (target) removeFolder(target);
                    }}
                    className="flex w-full cursor-pointer items-center gap-2 bg-transparent px-3 py-1.5 text-left text-[13px] text-status-error hover:bg-kumo-elevated"
                  >
                    <X size={13} />
                    Delete {treeMenu.folder.split("/").pop()}/
                  </button>
                ) : null}
              </div>
            </div>,
            document.body
          )
        : null}

      {/* "Open…" menu: everything the top bar no longer shows as a direct icon
          (resource explorers, HTTP/Console, Format, Watch) lives here. */}

      {/* Search file contents modal */}
      <Modal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        title="Search file contents"
        size="xl"
      >
        <div className="flex items-center gap-1.5 rounded-md border border-kumo-line px-2">
          <MagnifyingGlassIcon size={13} className="shrink-0 text-kumo-subtle" />
          <input
            aria-label="Search files"
            placeholder="Search across all files…"
            autoComplete="off"
            spellCheck={false}
            // biome-ignore lint: modal search field should focus on open
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-8 w-full min-w-0 bg-transparent text-[13px] text-kumo-default outline-none placeholder:text-kumo-placeholder"
          />
          {query ? (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setQuery("")}
              className="shrink-0 cursor-pointer bg-transparent text-kumo-subtle"
            >
              <X size={12} />
            </button>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          {searchResults ? (
            searchResults.total ? (
              <>
                <p className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wide text-kumo-subtle">
                  {searchResults.total} result
                  {searchResults.total === 1 ? "" : "s"} in{" "}
                  {searchResults.groups.length} file
                  {searchResults.groups.length === 1 ? "" : "s"}
                </p>
                {searchResults.groups.map((group) => (
                  <div key={group.file} className="mb-1">
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        openFile(group.file);
                        setSearchOpen(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          openFile(group.file);
                          setSearchOpen(false);
                        }
                      }}
                      className="flex cursor-pointer items-center gap-1.5 rounded px-1 py-[3px] text-xs text-kumo-default hover:bg-kumo-elevated"
                    >
                      <FileTypeIcon
                        path={group.file}
                        className="h-[14px] w-[14px]"
                      />
                      <span className="truncate font-mono">{group.file}</span>
                      <span className="ml-auto shrink-0 rounded-full bg-kumo-elevated px-1.5 text-[10px] text-kumo-subtle">
                        {group.matches.length}
                      </span>
                    </div>
                    {group.matches.map((match, i) => {
                      const preview = matchPreview(match);
                      return (
                        <div
                          key={`${match.line}-${match.start}-${i}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            revealMatch(match);
                            setSearchOpen(false);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              revealMatch(match);
                              setSearchOpen(false);
                            }
                          }}
                          title={`${group.file}:${match.line}`}
                          className="flex cursor-pointer items-center gap-2 rounded py-[2px] pl-7 pr-2 hover:bg-kumo-elevated"
                        >
                          <span className="w-10 shrink-0 text-right font-mono text-[10px] text-kumo-subtle">
                            {match.line}
                          </span>
                          <HighlightedPreview
                            file={group.file}
                            preview={preview}
                          />
                        </div>
                      );
                    })}
                  </div>
                ))}
              </>
            ) : (
              <p className="px-1 py-2 text-xs text-kumo-subtle">
                No results found
              </p>
            )
          ) : (
            <p className="px-1 py-2 text-xs text-kumo-subtle">
              Type to search the contents of every file in this workspace.
            </p>
          )}
        </div>
      </Modal>

      {/* Rename file modal */}
      <Modal
        open={renameFileOpen}
        onClose={closeRenameFile}
        title="Rename File"
        size="sm"
      >
        <Input
          aria-label="New file path"
          autoFocus
          spellCheck={false}
          value={renameFileName}
          onFocus={(event) => event.currentTarget.select()}
          onChange={(event) => setRenameFileName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") handleRenameFile();
          }}
        />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={closeRenameFile}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleRenameFile}>
            Rename
          </Button>
        </div>
      </Modal>

      {/* New folder modal */}
      <Modal
        open={newFolderOpen}
        onClose={() => setNewFolderOpen(false)}
        title="New Folder"
        size="sm"
      >
        <Input
          aria-label="New folder name"
          autoFocus
          placeholder="e.g., src/helpers"
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") createFolder();
          }}
        />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setNewFolderOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={createFolder}>
            Create Folder
          </Button>
        </div>
      </Modal>

      {/* Add file modal */}
      <Modal
        open={addFileOpen}
        onClose={() => setAddFileOpen(false)}
        title="Add New File"
        size="sm"
      >
        <Input
          aria-label="New file name"
          autoFocus
          placeholder="e.g., src/utils.ts"
          value={addFileName}
          onChange={(e) => setAddFileName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleAddFile();
          }}
        />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setAddFileOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddFile}>
            Add File
          </Button>
        </div>
      </Modal>
      <GlobalCommandPalette includeRegistry />
    </div>
    {/* Zero-size stacking context so kumo portals paint above the
        fixed EditorLayout panels (z-index 1). */}
    <div ref={setKumoPortalContainer} className="fixed z-[9999]" />
    </KumoPortalProvider>
    </OpencodeThemeProvider>
  );
}

const root = document.getElementById("root");
if (root) {
  // ?embed=<workspace>:<recordingId> renders the dedicated, iframe-embeddable
  // recording player (no IDE chrome) instead of the full workbench.
  const embedParams = new URLSearchParams(window.location.search);
  const embedTarget = Option.match(parseFlowRecordingRef(embedParams.get("embed")), { onNone: () => null, onSome: value => value });
  const shotRaw = embedParams.get("shot");
  const initialShot =
    shotRaw !== null && Number.isInteger(Number(shotRaw))
      ? Number(shotRaw)
      : undefined;
  void mountApplication(root, Layer.empty, () => (
    embedTarget ? (
      <EmbeddedRecordingPlayer
        workspaceId={embedTarget.workspaceId}
        recordingId={embedTarget.recordingId}
        initialShot={initialShot}
      />
    ) : (
      <App />
    )
  ), Effect.void).then(result => {
    if (!result.ok) {
      root.textContent = result.message;
    }
  });
}
